import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BookCard from './BookCard';
import { trackEvent } from '../services/analytics';
import '../styles/FacultyShowcasePage.css';

const PAGE_SIZE = 24;

function FacultyShowcasePage() {
  const navigate = useNavigate();
  const scrollToRef = useRef(null);
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchBooks = useCallback(async (pageNum) => {
    const offset = (pageNum - 1) * PAGE_SIZE;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/books/by-type/FB?limit=${PAGE_SIZE}&offset=${offset}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch faculty authored books');
      }

      setBooks(Array.isArray(data.books) ? data.books : []);
      setTotal(data.total || 0);
      setPage(pageNum);
    } catch (err) {
      setBooks([]);
      setTotal(0);
      setError(err.message || 'Failed to fetch faculty authored books');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackEvent('faculty_showcase_viewed', { source: 'faculty-showcase-page' });
    let startPage = 1;
    try {
      const saved = JSON.parse(sessionStorage.getItem('opac_faculty_back') || 'null');
      if (saved) {
        startPage = saved.page || 1;
        scrollToRef.current = saved.biblioId ? String(saved.biblioId) : null;
        sessionStorage.removeItem('opac_faculty_back');
      }
    } catch {}
    fetchBooks(startPage);
  }, [fetchBooks]);

  // Scroll to last-clicked card after books are rendered
  useEffect(() => {
    if (!books.length || !scrollToRef.current) return;
    const id = scrollToRef.current;
    scrollToRef.current = null;
    requestAnimationFrame(() => {
      document.getElementById(`book-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [books]);

  const handleBookClick = (book) => {
    trackEvent('faculty_showcase_book_clicked', { biblioId: book.biblio_id });
    sessionStorage.setItem('opac_faculty_back', JSON.stringify({ page, biblioId: book.biblio_id }));
    navigate(`/book/${book.biblio_id}`, { state: { from: 'faculty-showcase' } });
  };

  return (
    <section className="faculty-showcase-page">
      <div className="faculty-showcase-header">
        <button className="faculty-showcase-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1>IITH Faculty Authored Books</h1>
          <p className="faculty-showcase-subtitle">
            A showcase of titles written by IITH's own faculty members.
            {total > 0 ? ` ${total.toLocaleString()} books in collection.` : ''}
          </p>
        </div>
      </div>

      {error && <div className="faculty-showcase-error">{error}</div>}
      {loading && <div className="faculty-showcase-loading">Loading...</div>}

      {!loading && !error && books.length === 0 && (
        <div className="faculty-showcase-empty">No faculty-authored titles found.</div>
      )}

      {!loading && books.length > 0 && (
        <div className="faculty-showcase-grid">
          {books.map((book) => (
            <BookCard
              key={book.biblio_id}
              biblioId={book.biblio_id}
              isbn={book.isbn}
              title={book.title}
              author={book.author}
              onClick={() => handleBookClick(book)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && !loading && (
        <div className="faculty-showcase-pagination">
          <button className="faculty-showcase-page-btn" onClick={() => fetchBooks(1)} disabled={page === 1}>{'<<'}</button>
          <button className="faculty-showcase-page-btn" onClick={() => fetchBooks(page - 1)} disabled={page === 1}>{'< Prev'}</button>
          <span className="faculty-showcase-page-info">Page {page} of {totalPages}</span>
          <button className="faculty-showcase-page-btn" onClick={() => fetchBooks(page + 1)} disabled={page === totalPages}>{'Next >'}</button>
          <button className="faculty-showcase-page-btn" onClick={() => fetchBooks(totalPages)} disabled={page === totalPages}>{'>>'}</button>
        </div>
      )}
    </section>
  );
}

export default FacultyShowcasePage;
